/*------------------- Angular ---------------------------*/
import { APP_INITIALIZER, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { APP_BASE_HREF, CommonModule, PlatformLocation } from '@angular/common';
import { HttpClient, HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { ReactiveFormsModule } from '@angular/forms';
import { CookieModule } from 'ngx-cookie';
import { OverlayModule } from '@angular/cdk/overlay';

/*------------------ NDBX -------------------------------*/
import { NdbxIconModule } from '@allianz/ngx-ndbx/icon';
import { NxLinkModule } from '@aposin/ng-aquila/link';
import { NxHeaderModule } from '@aposin/ng-aquila/header';
import { NxFooterModule } from '@aposin/ng-aquila/footer';
import { NxBreadcrumbModule } from '@aposin/ng-aquila/breadcrumb';
import { NxHeadlineModule } from '@aposin/ng-aquila/headline';
import { NxCopytextModule } from '@aposin/ng-aquila/copytext';
import { NxGridModule } from '@aposin/ng-aquila/grid';
import { NxButtonModule } from '@aposin/ng-aquila/button';
import { NxPopoverModule } from '@aposin/ng-aquila/popover';
import { NxSpinnerModule } from '@aposin/ng-aquila/spinner';
import { NxIconModule } from '@aposin/ng-aquila/icon';
import { NxTabsModule } from '@aposin/ng-aquila/tabs';
import { NxModalModule } from '@aposin/ng-aquila/modal';
import { NxInputModule } from '@aposin/ng-aquila/input';
import { NxFormfieldModule } from '@aposin/ng-aquila/formfield';
import { NxCheckboxModule } from '@aposin/ng-aquila/checkbox';
import { NxDropdownModule } from '@aposin/ng-aquila/dropdown';
import { NxCardModule } from '@aposin/ng-aquila/card';
import { NxDatefieldModule } from '@aposin/ng-aquila/datefield';
import { NxNativeDateModule } from '@aposin/ng-aquila/datefield';
import { NxMomentDateModule } from '@aposin/ng-aquila/moment-date-adapter';
import { NxPaginationModule } from '@aposin/ng-aquila/pagination';
import { NxSmallStageModule } from '@aposin/ng-aquila/small-stage';
import { NxAccordionModule } from '@aposin/ng-aquila/accordion';
import { NxMessageModule } from '@aposin/ng-aquila/message';
import { UiComponentsModule } from 'az-customer-portal-ui';
import { NxRadioModule } from '@aposin/ng-aquila/radio-button';
import { NxTableModule } from '@aposin/ng-aquila/table';
import { NxAutocompleteModule } from '@aposin/ng-aquila/autocomplete';
import { NxNaturalLanguageFormModule } from '@aposin/ng-aquila/natural-language-form';
import { NxMaskModule } from '@aposin/ng-aquila/mask';
import { NxRadioToggleModule } from '@aposin/ng-aquila/radio-toggle';
import { NxAvatarModule } from '@aposin/ng-aquila/avatar';

/*--------------------- Application -----------------------------*/
// App imports
import { environment } from '../environments/environment';

// App root
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';

// Home component
import { HomeComponent } from './modules/home/home.component';
import { HomeIndividualComponent } from './modules/home/home-individual/home-individual.component';
import { HomeLegalPersonComponent } from './modules/home/home-legal-person/home-legal-person.component';
import { SpecialOfferComponent } from './modules/home/special-offer/special-offer.component';

// // Policies component
import { PoliciesOverviewComponent } from './modules/policies/policy-overview/policies-overview.component';
import { PolicyIndividualComponent } from './modules/policies/policy-overview/policy-individual/policy-individual.component';
import { PolicyIndividualCardComponent } from './modules/policies/policy-overview/policy-individual/policy-individual-card/policy-individual-card.component';
import { PolicyLegalPersonComponent } from './modules/policies/policy-overview/policy-legal-person/policy-legal-person.component';
import { PolicyLegalPersonCardComponent } from './modules/policies/policy-overview/policy-legal-person/policy-legal-person-card/policy-legal-person-card.component';

import { PolicyDetailComponent } from './modules/policies/policy-detail/policy-detail.component';
import { PolicyDetailIndividualComponent } from './modules/policies/policy-detail/policy-detail-individual/policy-detail-individual.component';
import { PolicyDetailLegalPersonComponent } from './modules/policies/policy-detail/policy-detail-legal-person/policy-detail-legal-person.component';

// Claims component
import { ClaimsComponent } from './modules/claims/claims.component';
import { ClaimCardComponent } from './modules/claims/claim-card/claim-card.component';
import { NewClaimComponent } from './modules/claims/new-claim/new-claim.component';
import { ClaimPolicyCardComponent } from './modules/claims/claim-policy-card/claim-policy-card.component';

// Contact us component
import { ContactUsComponent } from './modules/contact-us/contact-us.component';

// Inbox and Email us
import { InboxComponent } from './modules/inbox/inbox.component';
import { EmailUsComponent } from './modules/inbox/email-us/email-us.component';

// // Maintanence component
import { MaintenanceComponent } from './modules/maintenance/maintenance.component';

// Shared components
import { TopbarComponent } from './shared/components/topbar/topbar.component';
import { FooterComponent } from './shared/components/footer/footer.component';
import { CallMeBackComponent } from './shared/components/call-me-back/call-me-back.component';
import { PremiumListPagedComponent } from './shared/components/premium-list-paged/premium-list-paged.component';

import { CorrespondenceComponent } from './shared/components/correspondence/correspondence.component';
import { PaymentStatusComponent } from './shared/components/payment-status/payment-status.component';
import { PaymentFrequencyComponent } from './shared/components/payment-frequency/payment-frequency.component';
import { NotFoundComponent } from './modules/not-found/not-found.component';
import { AlertNotificationsComponent } from './shared/components/alert-notifications/alert-notifications.component';
import { CorrespondenceSimpleListComponent } from './shared/components/correspondence-simple-list/correspondence-simple-list.component';
import { FormattedLinkComponent } from './shared/components/formatted-link/formatted-link.component';
import { HelpComponent } from './shared/components/help/help.component';
import { PrefferedAgentComponent } from './shared/components/preffered-agent/preffered-agent.component';
import { PaymentMethodPopupComponent } from './shared/components/payment-method-popup/payment-method-popup.component';
import { MaInsuranceComponent } from './shared/components/ma-insurance/ma-insurance.component';

// Core
import { HttpModule } from './core/http.module';
import { HttpErrorInterceptor } from './core/interceptors/http-error.interceptor';
import { ENV_CONFIG } from './core/environment-config.interface';
import { LoadingInterceptor } from './core/interceptors/loading.interceptor ';
import { LoadingService } from './core/services/loading.service';
import { DateFormatPipe } from './core/pipes/date-format.pipe';
import { CurrencyFormatPipe } from './core/pipes/currency-format.pipe';
import { StringFormatPipe } from './core/pipes/string-format.pipe';
import { EmployeeFormatPipe } from '@core/pipes/employee-format.pipe';
import { AlertService } from './core/services/alert.service';
import { ModalcloseService } from './core/services/modalclose.service';
import { DateTimeFormatPipe } from './core/pipes/datetime-format.pipe';
import { BaseModuleComponent } from './modules/base-module/base-module.component';
import { InsuredTripsComponent } from './modules/policies/policy-detail/policy-detail-legal-person/insured-trips/insured-trips.component';
import { ModalComponent } from './shared/components/modal/modal.component';
import { GenerateReportComponent } from './shared/components/modal/generate-report/generate-report.component';
import { NewInsuredTripComponent } from './modules/policies/policy-detail/policy-detail-legal-person/insured-trips/new-insured-trip/new-insured-trip.component';
import { EditInsuredTripComponent } from './modules/policies/policy-detail/policy-detail-legal-person/insured-trips/edit-insured-trip/edit-insured-trip.component';
import { ConfirmInsuredTripCancelationComponent } from './shared/components/modal/confirm-insured-trip-cancelation/confirm-insured-trip-cancelation.component';
import { AlertUpdatedTripComponent } from './shared/components/modal/alert-updated-trip/alert-updated-trip.component';
import { EmployeesComponent } from './modules/policies/policy-detail/policy-detail-legal-person/employees/employees.component';
import { EmployeeComponent } from './shared/components/modal/employee/employee.component';
import { MaintenanceService } from '@core/services/maintenance.service';
import { DateFormatHTMLPipe } from '@core/pipes/date-format-html.pipe';
import { CancelPolicyComponent } from './modules/policies/policy-detail/policy-detail-individual/cancel-policy/cancel-policy.component';
import { StornoAlertComponent } from './shared/components/modal/storno-alert/storno-alert.component';
import { StornoSuccessComponent } from './shared/components/modal/storno-success/storno-success.component';
import { MaInsuranceCarComponent } from '@shared/components/ma-insurance/ma-insurance-car/ma-insurance-car.component';
import { MaInsuranceLifeComponent } from '@shared/components/ma-insurance/ma-insurance-life/ma-insurance-life.component';
import { MaInsuranceTravelComponent } from '@shared/components/ma-insurance/ma-insurance-travel/ma-insurance-travel.component';
import { MaInsuranceTravelRZCPComponent } from '@shared/components/ma-insurance/ma-insurance-travel-rzcp/ma-insurance-travel-rzcp.component';
import { TravelCardComponent } from '@shared/components/travel-card/travel-card.component';
import { GreenCardComponent } from '@shared/components/green-card/green-card.component';
import { Life01InsComponent } from '@shared/components/ma-insurance/ma-insurance-life/sub-types/life01-ins/life01-ins.component';
import { Life02InsRskComponent } from '@shared/components/ma-insurance/ma-insurance-life/sub-types/life02-ins-rsk/life02-ins-rsk.component';
import { Life03InsRskVehComponent } from '@shared/components/ma-insurance/ma-insurance-life/sub-types/life03-ins-rsk-veh/life03-ins-rsk-veh.component';
import { Life04InsRskRedirComponent } from '@shared/components/ma-insurance/ma-insurance-life/sub-types/life04-ins-rsk-redir/life04-ins-rsk-redir.component';
import { Life05InsAnnuityComponent } from '@shared/components/ma-insurance/ma-insurance-life/sub-types/life05-ins-annuity/life05-ins-annuity.component';
import { Life06InsDepositComponent } from '@shared/components/ma-insurance/ma-insurance-life/sub-types/life06-ins-deposit/life06-ins-deposit.component';
import { Life07InsHouseholdLiabComponent } from '@shared/components/ma-insurance/ma-insurance-life/sub-types/life07-ins-household-liab/life07-ins-household-liab.component';
import { Life08InsPmpComponent } from '@shared/components/ma-insurance/ma-insurance-life/sub-types/life08-ins-pmp/life08-ins-pmp.component';
import { LifeInsHeadlineComponent } from '@shared/components/ma-insurance/ma-insurance-life/sub-types/life-ins-headline/life-ins-headline.component';
import { LifeInsDateComponent } from '@shared/components/ma-insurance/ma-insurance-life/sub-types/life-ins-date/life-ins-date.component';
import { HomeB2BLeaseComponent } from '@modules/home/home-b2b-lease/home-b2b-lease.component';
import { PolicyB2bLeaseComponent } from '@modules/policies/policy-overview/policy-b2b-lease/policy-b2b-lease.component';
import { PolicyB2bLeaseCardComponent } from '@modules/policies/policy-overview/policy-b2b-lease/policy-b2b-lease-card/policy-b2b-lease-card.component';
import { PolicyDetailB2bLeaseComponent } from '@modules/policies/policy-detail/policy-detail-b2b-lease/policy-detail-b2b-lease.component';
import { ProfileComponent } from '@modules/profile/profile.component';
import { NxSwitcherModule } from '@aposin/ng-aquila/switcher';
import { PasswordChangeComponent } from '@shared/components/modal/password-change/password-change.component';
import { AccountCancelComponent } from '@shared/components/modal/account-cancel/account-cancel.component';
import { PasswordChangeEcComponent } from '@shared/components/modal/password-change-ec/password-change-ec.component';
import { CancelEcAgreementComponent } from '@shared/components/modal/cancel-ec-agreement/cancel-ec-agreement.component';
import { UnifyContactsComponent } from '@shared/components/modal/unify-contacts/unify-contacts.component';
import { ProfileEditComponent } from '@modules/profile/profile-edit/profile-edit.component';
import { UpdateMobileCheckPINComponent } from '@shared/components/modal/update-mobile-check-pin/update-mobile-check-pin.component';
import { CancelMKTComponent } from '@shared/components/modal/cancel-mkt/cancel-mkt.component';


import { NxFileUploaderModule } from '@aposin/ng-aquila/file-uploader'
import { PartnerDocumentPartComponent } from '@shared/components/modal/partner-document-part/partner-document-part.component';

/*--------------------- End of imports --------------------------*/


export function translateHttpLoaderFactory(http: HttpClient): TranslateHttpLoader {
    return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

// export const NX_MESSAGE_CONFIG = new InjectionToken<NxMessageToastService>('NxMessageToastService');

const NdbxModules = [
  NxHeaderModule,
  NxFooterModule,
  NxLinkModule,
  NxBreadcrumbModule,
  NxHeadlineModule,
  NxCopytextModule,
  NxGridModule,
  NxButtonModule,
  NxPopoverModule,
  NxSpinnerModule,
  NxIconModule,
  NxTabsModule,
  NxModalModule,
  NxInputModule,
  NxFormfieldModule,
  OverlayModule,
  NxNaturalLanguageFormModule,
  NxAutocompleteModule,
  NxCheckboxModule,
  NxDropdownModule,
  NxDatefieldModule,
  NxNativeDateModule,
  NxMomentDateModule,
  NxDropdownModule,
  NxCardModule,
  NxPaginationModule,
  NxSmallStageModule,
  NxAccordionModule,
  NxMessageModule,
  NxRadioModule,
  NxTableModule,
  NxMaskModule,
  NxFileUploaderModule,
  NxRadioToggleModule,
  NxAvatarModule,
  NxSwitcherModule
];

@NgModule({
  declarations: [
    AppComponent,
    BaseModuleComponent,
    HomeComponent,
    HomeIndividualComponent,
    HomeLegalPersonComponent,
    HomeB2BLeaseComponent,

    // overview
    PoliciesOverviewComponent,
    PolicyIndividualComponent,
    PolicyIndividualCardComponent,

    PolicyLegalPersonComponent,
    PolicyLegalPersonCardComponent,

    PolicyB2bLeaseComponent,
    PolicyB2bLeaseCardComponent,
    // detail
    PolicyDetailComponent,
    PolicyDetailIndividualComponent,
    PolicyDetailLegalPersonComponent,
    PolicyDetailB2bLeaseComponent,

    PremiumListPagedComponent,
    PaymentMethodPopupComponent,

    TopbarComponent,
    FooterComponent,
    // BaseComponent,
    CallMeBackComponent,
    ContactUsComponent,

    // UpcomingPremiumComponent,
    // PastPremiumComponent,
    DateFormatPipe,
    DateTimeFormatPipe,
    CurrencyFormatPipe,
    StringFormatPipe,
    EmployeeFormatPipe,
    CorrespondenceComponent,
    PaymentStatusComponent,
    PaymentFrequencyComponent,
    NotFoundComponent,
    AlertNotificationsComponent,
    SpecialOfferComponent,
    CorrespondenceSimpleListComponent,
    FormattedLinkComponent,

    ClaimsComponent,
    ClaimCardComponent,
    NewClaimComponent,
    ClaimPolicyCardComponent,
    HelpComponent,
    InboxComponent,
    PrefferedAgentComponent,
    EmailUsComponent,
    MaInsuranceComponent,
    MaInsuranceCarComponent,
    MaInsuranceLifeComponent,
    MaInsuranceTravelComponent,
    MaInsuranceTravelRZCPComponent,
    TravelCardComponent,
    GreenCardComponent,
    Life01InsComponent,
    Life02InsRskComponent,
    Life03InsRskVehComponent,
    Life04InsRskRedirComponent,
    Life05InsAnnuityComponent,
    Life06InsDepositComponent,
    Life07InsHouseholdLiabComponent,
    Life08InsPmpComponent,
    LifeInsHeadlineComponent,
    LifeInsDateComponent,

    MaintenanceComponent,
    InsuredTripsComponent,
    ModalComponent,
    GenerateReportComponent,
    NewInsuredTripComponent,
    EditInsuredTripComponent,
    ConfirmInsuredTripCancelationComponent,
    AlertUpdatedTripComponent,
    EmployeesComponent,
    EmployeeComponent,
    CancelPolicyComponent,
    StornoAlertComponent,
    StornoSuccessComponent,
    // Profile
    ProfileComponent,
    ProfileEditComponent,
    PasswordChangeComponent,
    AccountCancelComponent,
    PasswordChangeEcComponent,
    CancelEcAgreementComponent,
    UnifyContactsComponent,
    UpdateMobileCheckPINComponent,
    CancelMKTComponent,
    
    PartnerDocumentPartComponent,
  ],
  imports: [
    BrowserModule,
    CommonModule,
    CookieModule.forRoot(),
    BrowserAnimationsModule,
    AppRoutingModule,
    NdbxIconModule,
    ...NdbxModules,
    UiComponentsModule,
    ReactiveFormsModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: translateHttpLoaderFactory,
        deps: [HttpClient]
      }
    }),
    HttpModule.forRoot(environment),
  ],
  providers: [
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpErrorInterceptor,
      multi: true,
      deps: [ENV_CONFIG, MaintenanceService, AlertService, TranslateService]
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LoadingInterceptor,
      multi: true,
      deps: [LoadingService]
    },
    {
      provide: APP_BASE_HREF,
      useFactory: (s: PlatformLocation) => s.getBaseHrefFromDOM(),
      deps: [PlatformLocation]
    },
    DateFormatPipe,
    DateFormatHTMLPipe,
    DateTimeFormatPipe,
    CurrencyFormatPipe,
    StringFormatPipe,
    EmployeeFormatPipe,
    provideHttpClient(withInterceptorsFromDi()),
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }